import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Mail, Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import useSeo from "../hooks/useSeo";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useSeo({
    title: "Recuperar contraseña · Rivones",
    description: "Recibe un enlace para restablecer tu contraseña de Rivones.",
    canonical: "https://rivones.com/forgot-password",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Ingresa tu correo electrónico");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/password-reset/request", { email: email.trim() });
      setDone(true);
      if (data?.message) toast.success(data.message);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No pudimos procesar tu solicitud.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-sand flex items-center justify-center p-4 sm:p-8"
      data-testid="forgot-password-page"
    >
      <div className="w-full max-w-md">
        <Link
          to="/login"
          className="inline-flex items-center gap-1.5 text-sm text-neutral-500 hover:text-[#111111] mb-6 transition-colors"
          data-testid="forgot-back-to-login"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver al inicio de sesión
        </Link>

        <div className="bg-white border border-neutral-200 rounded-3xl p-7 sm:p-9 shadow-sm">
          {done ? (
            <div className="text-center" data-testid="forgot-success-state">
              <div className="inline-flex w-14 h-14 rounded-2xl bg-emerald-50 items-center justify-center mb-4">
                <CheckCircle2 className="w-7 h-7 text-emerald-600" />
              </div>
              <h1 className="font-heading font-bold text-2xl text-[#111111] mb-2">
                Revisa tu correo
              </h1>
              <p className="text-neutral-600 text-sm leading-relaxed">
                Si <span className="font-semibold text-[#111111]">{email}</span> tiene una cuenta
                en Rivones, te enviamos un enlace para restablecer tu contraseña. El enlace expira
                en 60 minutos.
              </p>
              <p className="text-xs text-neutral-400 mt-4">
                ¿No lo ves? Revisa tu carpeta de spam.
              </p>
              <Link
                to="/login"
                className="inline-flex items-center justify-center mt-7 px-5 py-2.5 rounded-full bg-[#111111] text-white text-sm font-medium hover:bg-neutral-800 transition-colors"
                data-testid="forgot-go-to-login"
              >
                Volver al login
              </Link>
            </div>
          ) : (
            <>
              <div className="inline-flex w-12 h-12 rounded-2xl bg-terracotta/10 items-center justify-center mb-4">
                <Mail className="w-6 h-6 text-terracotta" />
              </div>
              <h1 className="font-heading font-bold text-2xl sm:text-3xl text-[#111111] mb-2">
                Recupera tu contraseña
              </h1>
              <p className="text-neutral-600 text-sm mb-6 leading-relaxed">
                Ingresa el correo asociado a tu cuenta y te enviaremos un enlace para crear una
                nueva contraseña.
              </p>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="reset-email" className="text-sm font-medium text-[#111111]">
                    Correo electrónico
                  </Label>
                  <div className="relative mt-1.5">
                    <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                    <Input
                      id="reset-email"
                      type="email"
                      autoComplete="email"
                      placeholder="tu@correo.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-9 h-11"
                      required
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full h-11 bg-[#111111] hover:bg-neutral-800 text-white rounded-full font-medium"
                  data-testid="forgot-submit-btn"
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    "Enviar enlace de recuperación"
                  )}
                </Button>
              </form>
              <p className="text-xs text-neutral-400 mt-5 text-center">
                ¿No tienes cuenta?{" "}
                <Link to="/login?mode=register" className="text-terracotta font-semibold hover:underline">
                  Regístrate
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
